// src/services/ImageHandlerService.ts
import axios from "axios";

export interface DownloadResult {
  url: string;
  base64: string | null;
}

class ImageHandlerService {
  /**
   * Download multiple URLs and return them as base64 strings.
   */
  async downloadUrls(urls: string[] | null | undefined): Promise<DownloadResult[]> {
    const result: DownloadResult[] = [];
    if (!urls || urls.length === 0) return result;

    for (const url of urls) {
      if (!this.checkIsValidUrl(url)) {
        result.push({ url, base64: url }); // non-URL string passed, keep as-is
        continue;
      }

      const base64 = await this.downloadUrl(url);
      result.push({ url, base64 });
    }

    return result;
  }

  /**
   * Check if a string is a valid URL.
   */
  private checkIsValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Download a single URL and convert to base64.
   */
  private async downloadUrl(url: string): Promise<string | null> {
    try {
      const response = await axios.get<Blob>(url, { responseType: "blob" });

      return await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(response.data);
      });
    } catch (error) {
      console.warn("Axios request error:", error);
      return null;
    }
  }
}

export default new ImageHandlerService();
